import React, { JSX } from 'react';
import EventStatusTransitionDialogFrame from './EventStatusTransitionDialogFrame';

type Props = {
  open: boolean;
  eventId: string;
  eventName?: string;
  subtitleOverride?: string;
  /** Devices racked and progressing into the race. */
  rackedDeviceIds: string[];
  /** Collected but never racked — recalled to the retrieval list on transition. */
  retrievalDeviceIds: string[];
  /** Never collected — released back to the pool on transition. */
  releasedDeviceIds: string[];
  busy: boolean;
  onCancel: () => void;
  onProceed: () => void | Promise<void>;
};

function CountCard({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: 'emerald' | 'amber' | 'slate';
}): JSX.Element {
  const toneClass = {
    emerald:
      'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/30 dark:text-emerald-100',
    amber:
      'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-100',
    slate:
      'border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-200',
  }[tone];
  return (
    <div className={`rounded-lg border px-3 py-2 text-center ${toneClass}`}>
      <div className="text-xl font-bold tabular-nums">{count}</div>
      <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
}

/** Combined-mode races: DISTRIBUTION -> ACTIVE (RACKING is skipped; retrieval list is computed server-side). */
export default function CombinedDistributionToActiveConfirmDialog(props: Props): JSX.Element | null {
  const {
    open,
    eventId,
    eventName,
    subtitleOverride,
    rackedDeviceIds,
    retrievalDeviceIds,
    releasedDeviceIds,
    busy,
    onCancel,
    onProceed,
  } = props;
  const titleName =
    (subtitleOverride?.trim() ? subtitleOverride.trim() : null) ||
    (eventName?.trim() ? eventName : eventId);
  const hasRacked = rackedDeviceIds.length > 0;

  return (
    <EventStatusTransitionDialogFrame
      open={open}
      busy={busy}
      onCancel={onCancel}
      onProceed={onProceed}
      titleId="combined-distribution-to-active-title"
      eyebrow="Race start"
      title="Move to Active"
      subtitle={titleName}
      headerAccent={hasRacked ? 'emerald' : 'amber'}
      warningTone="danger"
      warningTitle="This operation cannot be undone"
      warningBody={
        <>
          Moving to <strong>Active</strong> starts the race from an operations perspective. Device runtime
          snapshots are taken and this transition cannot be reversed.
        </>
      }
      operationIntro={
        <p>
          This is a combined collection &amp; racking race, so it moves straight from{' '}
          <span className="font-semibold text-gray-800 dark:text-slate-200">Distribution</span> to{' '}
          <span className="font-semibold text-gray-800 dark:text-slate-200">Active</span>. Only racked
          devices continue into the race; the server pulls everything else out automatically.
        </p>
      }
      confirmPhrase="ACTIVE"
      confirmInputId="combined-distribution-to-active-confirm-input"
      proceedButtonText="Proceed to Active"
      proceedDisabledExtra={!hasRacked}
      proceedUseSuccessTone={hasRacked}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <CountCard label="Racked — into race" count={rackedDeviceIds.length} tone="emerald" />
          <CountCard label="Collected, not racked" count={retrievalDeviceIds.length} tone="amber" />
          <CountCard label="Never collected" count={releasedDeviceIds.length} tone="slate" />
        </div>
        {!hasRacked ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-100">
            No devices are racked yet. Rack at least one device via racking verification before moving to
            Active.
          </div>
        ) : null}
        {retrievalDeviceIds.length > 0 ? (
          <div className="rounded-lg border border-amber-200 dark:border-amber-800/60 overflow-hidden">
            <div className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100">
              These devices were collected but never racked — they go to the retrieval list
            </div>
            <ul className="max-h-40 divide-y divide-amber-100 overflow-y-auto dark:divide-amber-900/40">
              {retrievalDeviceIds.map((deviceId) => (
                <li key={deviceId} className="px-3 py-1.5 font-mono text-xs text-gray-900 dark:text-slate-100">
                  {deviceId}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {releasedDeviceIds.length > 0 ? (
          <p className="text-[11px] text-gray-500 dark:text-slate-400">
            {releasedDeviceIds.length} device{releasedDeviceIds.length === 1 ? '' : 's'} never collected —
            released back to the pool automatically.
          </p>
        ) : null}
      </div>
    </EventStatusTransitionDialogFrame>
  );
}
